import { useHistory, useParams } from "react-router-dom";
import { useOrderDetails } from "../../../../../hooks/useOrderDetails";
import {
	BackButton,
	ClientInfo,
	DiscountField,
	ModalStyled,
	NotInformed,
	OrderDetailsBody,
	OrderDetailsTitle,
	OrderDetailsWrapper,
	PaymentInfo,
	ProductsInfo,
	StatusField,
} from "./styles";
import { Dropdown } from "primereact/dropdown";
import { useEffect, useState } from "react";
import { formatCnpj, formatDate } from "../../../../../helpers";
import formatCurrency from "../../../../utils/formatCurrency";
import { profileDetails } from "../../../../services/profile.service";
import { Loader } from "../../../../components/loader";
import { IUserAccount } from "../../../../models/IUserAccount";
import { OrderMaterialList } from "./material-list";
import { FileOrderHandler } from "./file-order-handler";
import {
	addOrderItems,
	removeOrderItem,
	updateOrder,
	updateOrderStatus,
} from "../../../../services/manager/admin/orders.service";
import { toast } from "react-toastify";
import { MdOutlineEdit, MdOutlineWarning } from "react-icons/md";
import { CustomEditInput } from "./components/input-edit";
import { ModalAddOrderProduct } from "./modals/add-order-product";
import { ModalCheckPaymentInfo } from "./modals/payments-info";
import { useTranslation } from "react-i18next";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

const ToastErrorMsg = (closeToast: any, toastProps: any) => {
	return (
		<div>
			<p>
				<MdOutlineWarning
					size={20}
					style={{ verticalAlign: "sub" }}
				/>{" "}
				<b>Inserir Arquivos Necessários</b>
			</p>
			<p>
				Para continuar, faça o upload dos documentos obrigatórios: nota
				fiscal, contrato e boleto bancário.
			</p>
		</div>
	);
};

export const OrderDetails: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const { order, setOrder } = useOrderDetails(id);
	const history = useHistory();

	const { t: translate } = useTranslation();

	const [client, setClient] = useState<IUserAccount>();

	const [filesErrors, setFilesErros] = useState<boolean>(false);
	const [isEditing, setIsEditing] = useState<any>({
		payment_type: false,
		installments: false,
		discount: false,
	});

	const [installmentsOptions, setInstallmentsOptions] = useState<any[]>([]);
	const statusOptions = [
		{
			label: "Aberto",
			code: 1,
		},
		{
			label: "Em negociação",
			code: 5,
		},
		{
			label: "Processando",
			code: 2,
		},
		{
			label: "Cancelado",
			code: 3,
		},
		{
			label: "Concluído",
			code: 4,
		},
	];
	const [currentStatus, setCurrentStatus] = useState(statusOptions[0]);

	const paymentOptions = [
		{
			label: "Cartão de Crédito",
			value: 1,
		},
		{
			label: "Boleto",
			value: 2,
		},
	];

	const handleInstallmentsOptions = () => {
		if (order) {
			let installOpt: any[] = [];
			[...Array(12)].map((e, i) =>
				installOpt.push({
					label: `${i + 1}x de ${formatCurrency(
						order.amount / (i + 1)
					)}`,
					value: i + 1,
				})
			);

			if (installOpt.length > 0) {
				setInstallmentsOptions(installOpt);
			}
		}
	};

	/**
	 * updates no painel de dados do cliente
	 */
	useEffect(() => {
		if (order) {
			setCurrentStatus(
				statusOptions.find(
					(item) => item.code === order.order_status
				) ?? currentStatus
			);

			profileDetails(order.profile).then((response) => {
				setClient(response);
			});

			handleInstallmentsOptions();
		}
	}, [order]);

	const handleStatusUpdate = (event: any) => {
		let currentCode = event.value.code;

		if (currentCode === 2 || currentCode === 4) {
			if (!order?.payment_type || !order.installments) {
				setShowPaymentModal(true);
				return;
			}

			if (!order.boleto_file || !order.contract || !order.invoice) {
				toast.error(<ToastErrorMsg />, {
					icon: false,
				});
				setFilesErros(true);
				return;
			}
		}

		setCurrentStatus(event.value);

		updateOrderStatus(order!.id, currentCode).then((response) => {
			setOrder(response);
			toast.success(
				`Status do pedido ${
					order!.id
				} alterado para ${event.value.label.toLowerCase()}`
			);
		});
	};

	const onCancel = (field: string) => {
		setIsEditing({ ...isEditing, [field]: false });
	};

	const onSave = (body: any, field: string) => {
		updateOrder(order!.id, body).then((response) => {
			setOrder(response);
		});
		setIsEditing({ ...isEditing, [field]: false });
	};

	// Modals
	const [showAddMaterial, setShowAddMaterial] = useState<boolean>(false);
	const [loadingAddMaterial, setLoadingAddMaterial] =
		useState<boolean>(false);

	const handleAddProduct = (params: any) => {
		setLoadingAddMaterial(true);
		addOrderItems(order!.id, params)
			.then((resp) => {
				setOrder(resp);
				setShowAddMaterial(false);
			})
			.finally(() => {
				setLoadingAddMaterial(false);
			});
	};

	const handleRemoveProduct = (id: number) => {
		removeOrderItem(id).then((resp) => {
			setOrder(resp);
		});
	};

	// Payment Modal
	const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
	const [loadingPaymentModal, setLoadingPaymentModal] =
		useState<boolean>(false);
	const handlePaymentSave = (params: any) => {
		updateOrder(order!.id, params)
			.then((response) => {
				setOrder(response);
			})
			.finally(() => {
				setShowPaymentModal(false);
			});
	};

	return (
		<OrderDetailsWrapper>
			<OrderDetailsTitle>
				<div className="d-flex align-items-center">
					<BackButton
						onClick={() => {
							history.goBack();
						}}
					>
						<em className="material-icons-outlined">
							arrow_circle_left
						</em>
					</BackButton>
					<h1>Pedido: #{id}</h1>
				</div>

				<div className="d-flex align-items-center status">
					<span className="label">Atualizar Status: </span>
					<Dropdown
						options={statusOptions}
						value={currentStatus}
						onChange={handleStatusUpdate}
						disabled={
							currentStatus.code === 4 || currentStatus.code === 3
						}
					></Dropdown>
				</div>
			</OrderDetailsTitle>
			{order ? (
				<div>
					<OrderDetailsBody>
						<ClientInfo>
							<h3>Dados do cliente</h3>

							{client ? (
								<div className="info">
									<span className="label">Status:</span>
									<span className="value">
										<StatusField
											status={order.order_status}
										>
											{translate(
												`manager.orders.status.${order.order_status_display}`
											)}
										</StatusField>
									</span>

									<span className="label">Responsável:</span>
									<span className="value">{client.name}</span>
									<span className="label">E-mail:</span>
									<span className="value">
										{client.email}
									</span>
									<span className="label">Telefone:</span>
									<span className="value">
										{client.phone}
									</span>
									<span className="label">Razão Social:</span>
									<span className="value">
										{client.company.name}
									</span>
									<span className="label">CNPJ:</span>
									<span className="value">
										{formatCnpj(client.company.cnpj)}
									</span>
								</div>
							) : (
								<Loader></Loader>
							)}

							<div className="status">
								<span className="label">Contrato: </span>
								<FileOrderHandler
									order={order}
									field="contract"
									orderField={order.contract}
									errors={filesErrors}
								></FileOrderHandler>
							</div>
						</ClientInfo>

						<PaymentInfo>
							<h3>Faturamento</h3>

							<div className="info">
								<span className="label">Data de compra:</span>
								<span className="value">
									{formatDate(order.created_at)}
								</span>

								<span className="label">Canal:</span>
								<span className="value">
									{order.order_payment_method_display}
								</span>

								<span className="label">
									Forma de pagamento:
								</span>
								<span className="value">
									{isEditing.payment_type === false ? (
										<>
											<div className="w-100">
												{order.payment_type ? (
													<>
														{order.payment_type ===
														2
															? "Boleto"
															: `Crédito - ****${
																	order.card
																		?.last_digits ??
																	""
															  }`}
													</>
												) : (
													<NotInformed>
														Não informado
													</NotInformed>
												)}
											</div>
											<OverlayTrigger
												placement={"top"}
												overlay={
													<Tooltip>Editar</Tooltip>
												}
											>
												<div
													className="actions pointer"
													onClick={() =>
														setIsEditing({
															...isEditing,
															payment_type: true,
														})
													}
													hidden={
														currentStatus.code ===
															4 ||
														currentStatus.code === 3
													}
												>
													<MdOutlineEdit size={20} />
												</div>
											</OverlayTrigger>
										</>
									) : (
										<CustomEditInput
											field="payment_type"
											inputType="select"
											selectOptions={paymentOptions}
											onCancel={onCancel}
											onSave={onSave}
										/>
									)}
								</span>

								<span className="label">Parcelamento:</span>
								<span className="value">
									{isEditing.installments === false ? (
										<>
											<div className="w-100">
												{order.payment_type ? (
													<>
														{order.installments > 1
															? `Em ${
																	order.installments
															  }x de R$${formatCurrency(
																	order.amount /
																		order.installments
															  )} sem juros`
															: "À vista"}
													</>
												) : (
													<NotInformed>
														Não informado
													</NotInformed>
												)}
											</div>
											<OverlayTrigger
												placement={"top"}
												overlay={
													<Tooltip>Editar</Tooltip>
												}
											>
												<div
													className="actions"
													onClick={() =>
														setIsEditing({
															...isEditing,
															installments: true,
														})
													}
													hidden={
														currentStatus.code ===
															4 ||
														currentStatus.code === 3
													}
												>
													<MdOutlineEdit size={20} />
												</div>
											</OverlayTrigger>
										</>
									) : (
										<CustomEditInput
											field="installments"
											inputType="select"
											selectOptions={installmentsOptions}
											onCancel={onCancel}
											onSave={onSave}
										/>
									)}
								</span>

								<span className="label">Subtotal:</span>
								<span className="value">
									{formatCurrency(order.amount)}
								</span>

								<span className="label">Desconto:</span>
								<span className="value">
									{isEditing.discount === false ? (
										<>
											{order.discount ? (
												<>
													<DiscountField>
														-{" "}
														{formatCurrency(
															order.discount
														)}{" "}
														(
														{(
															(order.discount *
																100) /
															order.amount
														).toFixed(2)}
														%)
													</DiscountField>
												</>
											) : (
												<>
													<NotInformed>
														Nenhum cupom utilizado
													</NotInformed>
												</>
											)}
											<OverlayTrigger
												placement={"top"}
												overlay={
													<Tooltip>Editar</Tooltip>
												}
											>
												<div
													className="actions"
													onClick={() =>
														setIsEditing({
															...isEditing,
															discount: true,
														})
													}
													hidden={
														currentStatus.code ===
															4 ||
														currentStatus.code === 3
													}
												>
													<MdOutlineEdit size={20} />
												</div>
											</OverlayTrigger>
										</>
									) : (
										<CustomEditInput
											field="discount"
											inputType="price"
											onCancel={onCancel}
											onSave={onSave}
										/>
									)}
								</span>

								<span className="label">Total:</span>
								<span className="value">
									{formatCurrency(
										order.amount - order.discount
									)}
								</span>
							</div>

							<div className="invoice">
								<span className="label">
									Nota&nbsp;fiscal:{" "}
								</span>
								<FileOrderHandler
									order={order}
									field="invoice"
									orderField={order.invoice}
									errors={filesErrors}
								></FileOrderHandler>

								<span className="label">Boleto: </span>
								<FileOrderHandler
									order={order}
									field="boleto_file"
									orderField={order.boleto_file}
									errors={filesErrors}
								></FileOrderHandler>
							</div>
						</PaymentInfo>

						<ProductsInfo>
							{order.items && (
								<OrderMaterialList
									items={order.items}
									hasCoupons={false}
									onAddMaterial={() =>
										setShowAddMaterial(true)
									}
									onRemoveMaterial={handleRemoveProduct}
								></OrderMaterialList>
							)}
						</ProductsInfo>
					</OrderDetailsBody>

					<ModalStyled>
						<ModalAddOrderProduct
							show={showAddMaterial}
							isLoading={loadingAddMaterial}
							onSubmit={handleAddProduct}
							onHide={() => setShowAddMaterial(false)}
						/>
						<ModalCheckPaymentInfo
							order={order}
							show={showPaymentModal}
							isLoading={loadingPaymentModal}
							onSubmit={handlePaymentSave}
							onHide={() => setShowPaymentModal(false)}
						/>
					</ModalStyled>
				</div>
			) : (
				<Loader></Loader>
			)}
		</OrderDetailsWrapper>
	);
};
